<template>
  <app-layout
    :title="$t('common.fields.info')"
    :return-href="{
      path: 'executive.locations.show',
      properties: { id: solution.location.id },
    }"
  >
    <div class="overflow-hidden">
      <h5 class="text-h5 text-green-2 font-bold mx-7 mt-2 mb-6">
        {{ solution.solutionTemplate.name }}
      </h5>
      <div
        v-for="content in contents"
        :key="content.id"
        class="flex flex-col items-center sm:items-start mx-7 px-5 py-5 font-bold text-prime-green mb-8 bg-white rounded border border-prime-grey"
      >
        <h1
          class="uppercase text-b1 text-white bg-green-2 font-bold w-full px-4 flex justify-between"
        >
          <span>{{ content.name }}</span>
          <div v-if="content.isFromTemplate" class="flex flex-col items-center my-auto has-tooltip">
            <inline-svg
              :src="require('@/assets/link-solid.svg')"
              class="fill-current h-4 text-white"
            />
            <span class="tooltip rounded shadow-xl p-1 bg-black text-white -mt-24 normal-case">
              {{ $t("common.notices.contentFromTemplate") }}
            </span>
          </div>
        </h1>
        <div
          v-if="content.content"
          class="my-4 break-words w-full"
          v-html="content.content"
        />
        <div v-if="content.files && content.files.length" class="flex flex-wrap items-start w-full">
          <template v-for="file in addIndexForImageViewer(content.files)">
            <div
              v-if="file.imageIndex != null"
              :key="file.id"
              class="flex flex-col items-center w-1/2 p-4 sm:w-28 has-tooltip"
            >
              <img
                :src="
                  route('api.files.content', {
                    id: file.id,
                    filename: file.originalName,
                    thumbnail: !!file.thumbnailUrl
                  })
                "
                class="fill-current font-black inline-block cursor-pointer w-16 h-16"
                @click="
                  viewImageObject(addIndexForImageViewer(content.files), file)
                "
              />
              <span
                class="tooltip rounded shadow-xl p-1 bg-black text-white -mt-8"
                >{{ file.originalName }}</span
              >
            </div>
            <file
              v-else
              :key="file.id"
              :file="file"
              hide-file-name
              show-tooltip
            />
          </template>
        </div>
      </div>
      <div
      v-if="mappedAssets && mappedAssets.length" 
      class="flex flex-col items-center sm:items-start mx-7 px-5 py-5 font-bold text-prime-green mb-8 bg-white rounded border border-prime-grey">
        <h1
          class="uppercase text-b1 text-white bg-green-2 font-bold w-full px-4 flex justify-between"
        >
        <span>{{ $t('common.fields.assets') }}</span>
        </h1>
      <v-card-text  class="flex justify-center">
        <v-row class="flex flex-wrap items-center pt-4 gap-4">
          <v-card
          v-for="(asset, index) in mappedAssets"
            :key="index"
            class="h-96 w-72 border-solid border-2 border-green-2 text-green-2"
            variant="outlined"
          >
            <v-row no-gutters fill-height>
              <v-col cols="12">
                <v-card-text>
                  <v-row>
                    <v-col> 
                      <div class="p-2 overflow-hidden">
                        {{ asset.name }}
                      </div> 
                    </v-col>
                  </v-row>
                  <v-row
                  v-for="(assetAttribute, index2) in getAssetAttributes(asset).slice(0, 8)"
                  :key="index2">
                  <div v-if="assetAttribute.searchString">
                    <div class="px-2 overflow-hidden text-xs">
                      <span>{{  assetAttribute.attribute.name }}:</span>
                    </div>
                    <div class="px-2 pb-2 overflow-hidden text-xs font-normal">
                      <span>{{  assetAttribute.searchString }}</span>
                    </div>
                  </div>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-card>
        </v-row>
      </v-card-text>
      </div>
    </div>
  </app-layout>
</template>
<script>
import AppLayout from "@/Pages/AppLayout";
import File from "@/components/File.vue";
import { fixedAttributes } from "../../util/fixedAssetData"
import "@/assets/css/viewer.css";
import { api as viewerApi } from "v-viewer";

export default {
  components: { AppLayout, File },
  props: ["solution"],
  data() {
    return {
      organization: this.$page.props.auth.organization,
      desktopView: window.innerWidth >= 640,
    };
  },
  computed: {
    contents() {
      let contentList = [];

      const contents = this.solution?.contents?.map((x) => ({
        id: x.id,
        name: x.informationType.name,
        content: x.description,
        files: x.files,
      })) ?? [];

      contentList = contentList.concat(contents);

      const templateContents = this.solution?.solutionTemplate?.contents?.map((x) => ({
        id: x.id,
        name: x.informationType.name,
        content: x.description,
        files: x.files,
        isFromTemplate: true,
      })) ?? [];

      contentList = contentList.concat(templateContents);

      const attributeContents = this.solution?.attributeContents ?? [];

      contentList = contentList.concat(attributeContents);

      const templateAttributeContents = this.solution?.solutionTemplate?.attributeContents ?? [];

      templateAttributeContents.forEach((content) => {
        if (contentList.some((x) => x.attributeId === content.attributeId)) return;

        contentList.push({
          ...content,
          isFromTemplate: true,
        });
      });

      contentList.sort((a, b) => a.name.localeCompare(b.name));

      return contentList;
    },
    mappedAssets () {
      let mappedAssets = [];

      if (this.solution) {
        this.solution.assets.forEach((asset) => {
          let mappedAsset = {
            ...asset,
          };
          
          let currentNameAttribute = mappedAsset.assetAttributeLinks.find(
            (x) => x.attributeId === fixedAttributes.masterDataNameId
          );
          if (currentNameAttribute) {
            mappedAsset.name = currentNameAttribute.stringValue;
          }

          mappedAssets.push(mappedAsset);
        });
      }

        return mappedAssets;
    },
  },
  mounted() {
    window.addEventListener("resize", () => {
      this.desktopView = window.innerWidth >= 640;
    });
  },
  methods: {
    addIndexForImageViewer(files) {
      let index = 0;
      return files.map((obj) => {
        let newObj = {
          ...obj,
          imageIndex: this.isImage(obj) ? index : null,
          url: this.getRoute(obj),
          alt: obj.originalName,
        };
        index++;
        return newObj;
      });
    },
    getRoute(file) {
      return this.route('api.files.content', {
        id: file.id,
        filename: file.originalName
      })
    },
    isImage(file) {
      var extension = file.extension.toLowerCase();
      if ([".png", ".jpg", ".jpeg"].includes(extension)) {
        return true;
      }
      return false;
    },
    viewImageObject(filesWithImageIndex, file) {
      let imageArray = filesWithImageIndex.filter((x) => x.imageIndex != null);
      viewerApi({
        options: {
          toolbar: true,
          url: "url",
          initialViewIndex: file.imageIndex,
          title: (image) => {
            return image.alt;
          },
        },
        images: imageArray,
      });
    },
    getAssetAttributes(asset) {
      const fixedAttributeIds = Object.values(fixedAttributes);
      return asset.assetAttributeLinks.filter(link => fixedAttributeIds.includes(link.attributeId));
    },
  },
};
</script>
